import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "../ui/sheet";
import { useSite } from "../../context/SiteContext";
import { waLink } from "../../lib/api";
import { scrollToId, WhatsAppIcon } from "./shared";

const LINKS = [
  { label: "Início", id: "inicio" },
  { label: "Frei Lucas", id: "frei-lucas" },
  { label: "Cerimônias", id: "cerimonias" },
  { label: "Casamentos", id: "casamentos" },
  { label: "Depoimentos", id: "depoimentos" },
  { label: "Blog", id: "blog", route: "/blog" },
  { label: "Galeria", id: "galeria" },
  { label: "Perguntas Frequentes", id: "faq" },
  { label: "Consulte sua Data", id: "consulte" },
  { label: "Contato", id: "contato" },
];

export default function Header() {
  const { content } = useSite();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (link) => {
    setOpen(false);
    if (link.route) {
      navigate(link.route);
      return;
    }
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(link.id), 500);
    } else {
      scrollToId(link.id);
    }
  };

  const onDark = !scrolled && location.pathname === "/";
  const wa = content?.contacts;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || location.pathname !== "/"
          ? "bg-olivedark/90 backdrop-blur-md shadow-lg shadow-black/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[72px]">
          <button
            data-testid="nav-logo"
            onClick={() => go({ id: "inicio" })}
            className="text-left leading-none group"
          >
            <span className="block font-serif text-lg sm:text-xl tracking-wide text-cream group-hover:text-goldlight transition-colors whitespace-nowrap">
              PADRE LUCAS MACIEIRA
            </span>
            <span className="block text-[10px] uppercase tracking-[0.35em] text-goldlight/80 mt-1 whitespace-nowrap">
              Celebrante de Casamentos
            </span>
          </button>

          <nav className="hidden xl:flex items-center gap-5" data-testid="nav-desktop">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l)}
                className="text-[13px] font-medium tracking-wide text-cream/85 hover:text-goldlight transition-colors whitespace-nowrap"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              data-testid="nav-whatsapp-button"
              href={wa ? waLink(wa.whatsapp_number, wa.whatsapp_message) : "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-2 bg-whatsapp hover:bg-whatsappdark text-white text-[13px] font-semibold px-4 py-2.5 rounded-full transition-all duration-300 hover:scale-[1.03] active:scale-[0.98]"
            >
              <WhatsAppIcon className="w-4 h-4" />
              FALAR NO WHATSAPP
            </a>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid="nav-mobile-menu-button"
                  className="xl:hidden text-cream p-2"
                  aria-label="Abrir menu"
                >
                  <Menu className="w-6 h-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-olivedark border-l border-gold/20 w-[300px]">
                <SheetTitle className="font-serif text-cream text-xl mb-6">Menu</SheetTitle>
                <nav className="flex flex-col gap-1" data-testid="nav-mobile">
                  {LINKS.map((l) => (
                    <button
                      key={l.id}
                      data-testid={`nav-mobile-link-${l.id}`}
                      onClick={() => go(l)}
                      className="text-left text-cream/85 hover:text-goldlight hover:bg-white/5 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors"
                    >
                      {l.label}
                    </button>
                  ))}
                  <a
                    data-testid="nav-mobile-whatsapp"
                    href={wa ? waLink(wa.whatsapp_number, wa.whatsapp_message) : "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center justify-center gap-2 bg-whatsapp text-white text-sm font-semibold px-4 py-3 rounded-full"
                  >
                    <WhatsAppIcon className="w-4 h-4" />
                    FALAR NO WHATSAPP
                  </a>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
